import { ToasterHelpers } from "../../more/Helpers";
import Toaster from "../../moreCore/UI/Toaster_TS/Toaster";

/**
 * evisio2 one-time server-config check.
 *
 * On first evisio2 load per browser, asks the backend status endpoint whether the IIS app
 * pool is configured the way the MDSC function relay needs (StartMode=AlwaysRunning,
 * IdleTimeout=0). If the server *definitively* reports a sub-optimal config, shows a single
 * dismissible toast warning of a possibly degraded experience.
 *
 * Deliberately fails silent: a missing endpoint (old backend), a non-OK response, a parse
 * error or a network reject all result in no toast and no thrown error. "Can't verify"
 * (iisConfig null / Checked:false — debug build, IIS Express, insufficient permissions) is
 * NOT treated as misconfigured, so devs are never nagged.
 *
 * Isolated evisio2 module — touches no shared infrastructure.
 */

// Persisted "we already ran this check" flag. localStorage (cross-session) is preferred,
// sessionStorage is the fallback when localStorage is blocked, and an in-memory window flag
// is the last resort when both storages are inaccessible (private mode / sandboxed embeds).
const FLAG_KEY = "evisio2_iisConfigChecked";
const TRANSLATION_KEY = "Evisio2__Toast_ServerConfigSuboptimal";
const STATUS_PATH = "extras_api/mdscRelayStatus";

function hasBeenChecked(): boolean {
    try { if (localStorage.getItem(FLAG_KEY) === "1") return true; } catch { /* storage blocked */ }
    try { if (sessionStorage.getItem(FLAG_KEY) === "1") return true; } catch { /* storage blocked */ }
    try { if ((window as any).EvisioIISConfigChecked === true) return true; } catch { /* ignore */ }
    return false;
}

function markChecked(): void {
    try { localStorage.setItem(FLAG_KEY, "1"); return; } catch { /* fall through */ }
    try { sessionStorage.setItem(FLAG_KEY, "1"); return; } catch { /* fall through */ }
    try { (window as any).EvisioIISConfigChecked = true; } catch { /* nothing left to try */ }
}

function showDegradedToast(): void {
    let toaster: MdsLax<InstanceType<typeof Toaster>> = (window as any).toaster;
    if (!toaster || typeof toaster.addToast !== "function") return; // outer shell normally has it

    let translations: any = (window as any).translations;
    let msg: string = (translations && translations[TRANSLATION_KEY])
        || "Server configuration warning: IIS may not be optimally configured for evisio. You might experience a degraded experience.";

    toaster.addToast(msg, ToasterHelpers.ToasterIcons.warning, {
        timeout: 40*1000,
        closeButton: true,
        ID: "evisio2ServerConfig",
        /*
        onClick: ()=>{
        }
        */
    });
}

/**
 * Fire-and-forget. Safe to call unconditionally on boot — internally no-ops if already run,
 * and never rejects.
 */
export function runServerConfigCheckOnce(): void {
    try {
        if (hasBeenChecked()) return;

        let appRoot: string = (window as any).appRoot || "/";
        let url: string = appRoot + STATUS_PATH; // appRoot carries a trailing slash, STATUS_PATH has none

        fetch(url)
            .then((res) => {
                // Non-OK (e.g. 404 on an old backend) → silent, and do NOT mark checked, so a
                // later deploy of the endpoint gets picked up on the next visit.
                if (!res.ok) return null;
                return res.json().catch(() => null); // parse failure → treated as "no answer"
            })
            .then((data: any) => {
                if (data == null) return; // non-OK or unparseable → leave flag unset, stay silent

                // Endpoint answered cleanly — suppress future fetches regardless of verdict.
                markChecked();

                let cfg = data.iisConfig;
                if (cfg && cfg.Checked === true && cfg.RecommendedConfigForFunctionRelayMet === false) {
                    showDegradedToast();
                }
            })
            .catch(() => { /* network reject / endpoint absent — silent, flag left unset */ });
    } catch { /* never let this leak into boot */ }
}
